// Home.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import HoCarousel from './HoCarousel';
import HoFeatures from './HoFeatures';

const YOUTUBE_VIDEO_ID = 'H6N41pQeac4';

const styles = {
  ctaSection: {
    padding: '80px 0',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  ctaTitle: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
  },
  ctaDescription: {
    fontSize: '1.2rem',
    marginBottom: '40px',
    maxWidth: '600px',
    margin: '0 auto 40px',
    lineHeight: '1.6',
  },
  ctaButton: {
    display: 'inline-block',
    padding: '16px 40px',
    background: 'linear-gradient(45deg, #ff6b6b, #feca57)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '50px',
    fontWeight: '600',
    fontSize: '1.1rem',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
    border: 'none',
    cursor: 'pointer',
  },
  statsSection: {
    padding: '60px 0',
    backgroundColor: 'white',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '40px',
    textAlign: 'center',
  },
  statCard: { padding: '20px' },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#667eea',
    marginBottom: '10px',
  },
  statLabel: {
    fontSize: '1rem',
    color: '#64748b',
    fontWeight: '500',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
  },
  videoSection: {
    padding: '80px 0',
    background: 'linear-gradient(180deg, #f8f9ff 0%, #eef0ff 100%)',
    textAlign: 'center',
  },
  videoSectionTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1e1b4b',
    marginBottom: '12px',
  },
  videoSectionSubtitle: {
    fontSize: '1.1rem',
    color: '#64748b',
    marginBottom: '48px',
    maxWidth: '500px',
    margin: '0 auto 48px',
    lineHeight: '1.6',
  },

  // ── Two-phones layout ──────────────────────────────────────────────────────
  phonesRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '48px',
    flexWrap: 'wrap',       // stacks on narrow viewports
  },
  phoneCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '18px',
  },
  phoneLabel: {
    fontSize: '0.88rem',
    fontWeight: '700',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: '#7c86b0',
  },
  phoneBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 14px',
    borderRadius: '50px',
    fontSize: '0.78rem',
    fontWeight: '700',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
  },
  // ──────────────────────────────────────────────────────────────────────────

  phoneWrapper: {
    display: 'inline-block',
    position: 'relative',
  },
  phoneFrame: {
    width: '280px',
    height: '560px',
    background: '#1a1a2e',
    borderRadius: '44px',
    padding: '14px 12px',
    boxShadow: `
      0 0 0 2px #2d2d4e,
      0 0 0 4px #1a1a2e,
      0 30px 80px rgba(0,0,0,0.35),
      0 10px 30px rgba(102,126,234,0.25)
    `,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  phoneNotch: {
    width: '90px',
    height: '22px',
    background: '#1a1a2e',
    borderRadius: '0 0 16px 16px',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
  },
  phoneScreen: {
    width: '100%',
    height: '100%',
    borderRadius: '34px',
    overflow: 'hidden',
    background: '#000',
  },
  phoneVolUp:   { top: '100px', height: '40px', left: '-4px',  position: 'absolute', width: '4px', borderRadius: '3px 0 0 3px', background: '#2d2d4e' },
  phoneVolDown: { top: '155px', height: '40px', left: '-4px',  position: 'absolute', width: '4px', borderRadius: '3px 0 0 3px', background: '#2d2d4e' },
  phonePower:   { position: 'absolute', top: '130px', right: '-4px', height: '60px', width: '4px', borderRadius: '0 3px 3px 0', background: '#2d2d4e' },
};

// ---------------------------------------------------------------------------
// YouTubeFacade — loads the iframe ONLY when the user clicks play.
// Removes ~500 KB of YouTube JS/CSS from the critical path.
// ---------------------------------------------------------------------------
function YouTubeFacade({ videoId, title }) {
  const [playing, setPlaying] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  useEffect(() => {
    const origins = ['https://www.youtube.com', 'https://i.ytimg.com'];
    const links = origins.map((origin) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      document.head.appendChild(link);
      return link;
    });
    return () => {
      links.forEach((link) => {
        if (document.head.contains(link)) document.head.removeChild(link);
      });
    };
  }, []);

  if (playing) {
    return (
      <iframe
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  const thumbSrc = thumbError
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <button
      onClick={() => setPlaying(true)}
      aria-label={`Play ${title}`}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        padding: 0,
        border: 'none',
        cursor: 'pointer',
        background: '#000',
        position: 'relative',
      }}
    >
      <img
        src={thumbSrc}
        alt={title}
        loading="lazy"
        decoding="async"
        onError={() => setThumbError(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      {/* YouTube-style play button */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '52px',
          height: '36px',
          background: 'rgba(220, 0, 0, 0.92)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <polygon points="5,3 19,12 5,21" />
        </svg>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// PhoneFrame — reusable shell (notch + buttons + screen slot)
// ---------------------------------------------------------------------------
function PhoneFrame({ children }) {
  return (
    <div style={styles.phoneWrapper}>
      <div style={styles.phoneFrame}>
        <div style={styles.phoneNotch} />
        <div style={styles.phoneVolUp} />
        <div style={styles.phoneVolDown} />
        <div style={styles.phonePower} />
        <div style={styles.phoneScreen}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QRPhone — static QR-code image displayed inside a phone mockup.
// Place your QR image at public/qr.png (or update the src below).
// ---------------------------------------------------------------------------
function QRPhone({ lang }) {
  const alt = lang === 'en'
    ? 'Scan QR code to take the emoji survey'
    : 'Escanea el código QR para responder la encuesta';

  return (
    <PhoneFrame>
      {/* White card background so the QR code reads cleanly */}
      <div style={{
        width: '100%',
        height: '100%',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '24px',
        boxSizing: 'border-box',
      }}>
        {/* Top label inside the screen */}
        <p style={{
          margin: 0,
          fontSize: '0.72rem',
          fontWeight: '700',
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: '#667eea',
        }}>
          {lang === 'en' ? 'Scan & Respond' : 'Escanea y Responde'}
        </p>

        {/* QR image — replace qr.png with your actual file */}
        <img
          src={`${process.env.PUBLIC_URL}/qr.png`}
          alt={alt}
          loading="lazy"
          decoding="async"
          style={{
            width: '100%',
            maxWidth: '200px',
            height: 'auto',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(102,126,234,0.15)',
          }}
        />

        {/* Small emoji hint */}
        <p style={{
          margin: 0,
          fontSize: '1.4rem',
          lineHeight: 1,
        }}>
          😊 ⭐ 🎚️
        </p>

        <p style={{
          margin: 0,
          fontSize: '0.68rem',
          color: '#94a3b8',
          textAlign: 'center',
          lineHeight: 1.5,
        }}>
          {lang === 'en'
            ? 'Point your camera at the QR to open the survey instantly'
            : 'Apunta tu cámara al QR para abrir la encuesta al instante'}
        </p>
      </div>
    </PhoneFrame>
  );
}

// ---------------------------------------------------------------------------
// PhoneVideo — video phone + QR phone, side by side
// ---------------------------------------------------------------------------
function PhoneVideo({ lang }) {
  const title = lang === 'en' ? 'See It in Action' : 'Míralo en Acción';
  const subtitle = lang === 'en'
    ? 'Watch the demo and scan the QR to try your first survey right now.'
    : 'Mira el demo y escanea el QR para probar tu primera encuesta ahora mismo.';

  const videoLabel = lang === 'en' ? '▶ Watch demo' : '▶ Ver demo';
  const qrLabel    = lang === 'en' ? '📲 Try it now' : '📲 Pruébalo ya';

  return (
    <section style={styles.videoSection}>
      <div style={styles.container}>
        <h2 style={styles.videoSectionTitle}>{title}</h2>
        <p style={styles.videoSectionSubtitle}>{subtitle}</p>

        <div style={styles.phonesRow}>

          {/* ── Phone 1: YouTube demo video ── */}
          <div style={styles.phoneCol}>
            {/* Badge above phone */}
            <span style={{
              ...styles.phoneBadge,
              background: 'rgba(220, 0, 0, 0.08)',
              color: '#dc0000',
            }}>
              ▶ {lang === 'en' ? 'Demo' : 'Demo'}
            </span>

            <PhoneFrame>
              <YouTubeFacade
                videoId={YOUTUBE_VIDEO_ID}
                title="EmojiSurveys demo"
              />
            </PhoneFrame>

            <span style={styles.phoneLabel}>{videoLabel}</span>
          </div>

          {/* ── Phone 2: QR code ── */}
          <div style={styles.phoneCol}>
            {/* Badge above phone */}
            <span style={{
              ...styles.phoneBadge,
              background: 'rgba(102, 126, 234, 0.10)',
              color: '#667eea',
            }}>
              📲 {lang === 'en' ? 'Live Survey' : 'Encuesta Real'}
            </span>

            <QRPhone lang={lang} />

            <span style={styles.phoneLabel}>{qrLabel}</span>
          </div>

        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------
function Home({ onNavigate }) {
  const { language, currentUser } = useAuth();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const translations = {
    es: {
      carouselItems: [
        { id: 1, emoji: '😊', title: 'Encuestas Divertidas con Emojis',        description: 'Captura la satisfacción del cliente usando emojis de caritas. Haz que responder sea divertido y fácil.',                                                                imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen1.png`, fallbackColor: '#667eea' },
        { id: 2, emoji: '⭐', title: 'Evaluaciones con Estrellas y Sliders',   description: 'Múltiples formas de evaluar: estrellas, sliders, opciones múltiples. Todo personalizable según tus necesidades.',                                                          imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen2.png`, fallbackColor: '#764ba2' },
        { id: 3, emoji: '📊', title: 'Resultados Instantáneos',                description: 'Obtén análisis en tiempo real con gráficos dinámicos. Crea en minutos y comparte con tus clientes.',                                                                        imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen3.png`, fallbackColor: '#ff6b6b' },
        { id: 4, emoji: '🎨', title: 'Diseño Personalizable',                  description: 'Personaliza colores, fuentes y estilos para que tu encuesta se vea como tu marca. Crea experiencias únicas y memorables.',                                                  imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen4.png`, fallbackColor: '#ff9a9e' },
      ],
      features: [
        { icon: '😍', title: 'Encuestas con Emojis',          description: 'Utiliza emojis de caritas para hacer las encuestas más atractivas y obtener respuestas honestas sobre la satisfacción del cliente.' },
        { icon: '⭐', title: 'Múltiples Tipos de Evaluación', description: 'Estrellas, sliders, opciones múltiples (2 o 4 opciones), escalas del 1 al 5. Todo completamente personalizable.' },
        { icon: '📈', title: 'Análisis Inteligente',          description: 'Resultados en tablas y gráficos dinámicos. Identifica tendencias y áreas de mejora con análisis en tiempo real.' },
        { icon: '⚡', title: 'Creación Rápida',               description: 'Crea encuestas profesionales en minutos. Combina preguntas abiertas, opción múltiple y escalas de calificación.' },
        { icon: '📱', title: 'Feedback Inmediato',            description: 'Obtén respuestas instantáneas y comunica los resultados a tus clientes para mostrar que sus opiniones son valoradas.' },
        { icon: '🎯', title: 'Fácil de Compartir',            description: 'Distribuye tus encuestas por correo, redes sociales o intégralas en tu sitio web. Maximiza tu tasa de respuesta.' },
      ],
      stats: [
        { number: '10K+', label: 'Encuestas Creadas' },
        { number: '95%',  label: 'Satisfacción Cliente' },
        { number: '2 min',label: 'Tiempo Promedio' },
        { number: '24/7', label: 'Soporte Disponible' },
      ],
      ctaEmoji: '🎉',
      ctaTitle: '¡Comienza a Recopilar Feedback Hoy!',
      ctaDescription: 'Únete a miles de empresas que ya utilizan EmojiSurveys para mejorar la experiencia de sus clientes. Crea tu primera encuesta gratis en menos de 2 minutos.',
      ctaButtonText: '✨ Crear Cuenta Gratis',
    },
    en: {
      carouselItems: [
        { id: 1, emoji: '😊', title: 'Fun Surveys with Emojis',            description: 'Capture customer satisfaction using face emojis. Make responding fun and easy.',                                                imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen1.png`, fallbackColor: '#667eea' },
        { id: 2, emoji: '⭐', title: 'Evaluations with Stars and Sliders', description: 'Multiple ways to evaluate: stars, sliders, multiple options. All customizable according to your needs.',                       imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen2.png`, fallbackColor: '#764ba2' },
        { id: 3, emoji: '📊', title: 'Instant Results',                    description: 'Get real-time analysis with dynamic charts. Create in minutes and share with your customers.',                                  imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen3.png`, fallbackColor: '#ff6b6b' },
        { id: 4, emoji: '🎨', title: 'Customizable Design',                description: 'Customize colors, fonts, and styles so your survey looks like your brand. Create unique and memorable experiences.',          imageUrl: `${process.env.PUBLIC_URL}/imgpresentacion/imagen4.png`, fallbackColor: '#ff9a9e' },
      ],
      features: [
        { icon: '😍', title: 'Surveys with Emojis',          description: 'Use face emojis to make surveys more attractive and get honest responses about customer satisfaction.' },
        { icon: '⭐', title: 'Multiple Evaluation Types',     description: 'Stars, sliders, multiple options (2 or 4 options), scales from 1 to 5. All fully customizable.' },
        { icon: '📈', title: 'Smart Analysis',               description: 'Results in tables and dynamic charts. Identify trends and areas for improvement with real-time analysis.' },
        { icon: '⚡', title: 'Quick Creation',               description: 'Create professional surveys in minutes. Combine open questions, multiple choice, and rating scales.' },
        { icon: '📱', title: 'Immediate Feedback',           description: 'Get instant responses and communicate the results to your customers to show that their opinions are valued.' },
        { icon: '🎯', title: 'Easy to Share',                description: 'Distribute your surveys by email, social networks, or integrate them into your website. Maximize your response rate.' },
      ],
      stats: [
        { number: '10K+', label: 'Surveys Created' },
        { number: '95%',  label: 'Customer Satisfaction' },
        { number: '2 min',label: 'Average Time' },
        { number: '24/7', label: 'Available Support' },
      ],
      ctaEmoji: '🎉',
      ctaTitle: 'Start Collecting Feedback Today!',
      ctaDescription: "Join thousands of companies already using EmojiSurveys to improve their customers' experience. Create your first survey for free in less than 2 minutes.",
      ctaButtonText: '✨ Create Free Account',
    },
  };

  const lang = language === 'es' ? 'es' : 'en';
  const content = translations[lang];

  const resultCards = [
    { file: 'Result1.png', label: 'Response Overview' },
    { file: 'Result2.png', label: 'Satisfaction Metrics' },
    { file: 'Result3.png', label: 'Trend Analysis' },
    { file: 'Result4.png', label: 'Detailed Breakdown' },
  ];

  return (
    <div>
      {/* Hero Carousel */}
      <HoCarousel
        carouselItems={content.carouselItems}
        height={isMobile ? '65vh' : '75vh'}
      />

      {/* Video phone + QR phone */}
      <PhoneVideo lang={lang} />

      {/* Stats */}
      <section style={styles.statsSection}>
        <div style={styles.container}>
          <div style={styles.statsGrid}>
            {content.stats.map((stat, index) => (
              <div key={index} style={styles.statCard}>
                <div style={styles.statNumber}>{stat.number}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <HoFeatures features={content.features} />

      {/* Results Images */}
      <section style={{
        padding: '80px 0',
        background: 'linear-gradient(180deg, #ffffff 0%, #f4f6ff 100%)',
        textAlign: 'center',
      }}>
        <div style={styles.container}>
          <div style={{ marginBottom: '56px' }}>
            <span style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              fontSize: '0.78rem',
              fontWeight: '700',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              padding: '6px 18px',
              borderRadius: '50px',
              marginBottom: '20px',
            }}>
              Live Dashboard
            </span>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: '800',
              color: '#1e1b4b',
              marginBottom: '16px',
              lineHeight: '1.2',
            }}>
              Real-Time Survey Results
            </h2>
            <p style={{
              fontSize: '1.05rem',
              color: '#64748b',
              maxWidth: '520px',
              margin: '0 auto',
              lineHeight: '1.7',
            }}>
              Instantly visualize your data with clean, intuitive dashboards —
              no technical skills required. Designed to be easy, friendly, and
              professional from day one.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '28px',
          }}>
            {resultCards.map((item, index) => (
              <div
                key={index}
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(102,126,234,0.10)',
                  border: '1px solid rgba(102,126,234,0.10)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 16px 48px rgba(102,126,234,0.20)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 24px rgba(102,126,234,0.10)';
                }}
              >
                <div style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f1f3ff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#fafbff',
                }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
                  </div>
                  <span style={{
                    fontSize: '0.78rem',
                    fontWeight: '600',
                    color: '#94a3b8',
                    marginLeft: '6px',
                    letterSpacing: '0.5px',
                  }}>
                    {item.label}
                  </span>
                </div>
                <img
                  src={`${process.env.PUBLIC_URL}/result/${item.file}`}
                  alt={item.label}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            ))}
          </div>

          <p style={{
            marginTop: '48px',
            fontSize: '0.95rem',
            color: '#94a3b8',
            letterSpacing: '0.3px',
          }}>
            ✦ All charts update automatically. ✦
          </p>
        </div>
      </section>

      {/* Call to Action */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>{content.ctaEmoji}</div>
          <h2 style={styles.ctaTitle}>{content.ctaTitle}</h2>
          <p style={styles.ctaDescription}>{content.ctaDescription}</p>
          <button
            style={styles.ctaButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)';
            }}
            onClick={() => {
              if (onNavigate) onNavigate('register');
              else window.location.hash = 'register';
            }}
          >
            {content.ctaButtonText}
          </button>
        </div>
      </section>
    </div>
  );
}

export default Home;